import { BaseClient } from "../../clients/base/BaseClient";
import { TESTS_ENDPOINTS } from "./tests.consts";
import type {
  TestsResponse,
  TestResponse,
  TestsQueryParams,
  TestsByInstrumentParams,
  TestCreateRequest,
  TestUpdateRequest,
} from "./tests.types";

/**
 * Tests API Client
 * Handles all test management operations.
 */
export class TestsClient extends BaseClient {
  /**
   * Fetch all tests with optional filtering.
   * @param {TestsQueryParams} params - Optional query parameters.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<TestsResponse>} Array of tests.
   */
  async fetchTests(params?: TestsQueryParams, signal?: AbortSignal | null): Promise<TestsResponse> {
    return this.get<TestsResponse>(TESTS_ENDPOINTS.TESTS, {
      params,
      signal,
    });
  }

  /**
   * Fetch a specific test by ID.
   * @param {number} id - Test ID.
   * @param {TestsQueryParams} params - Optional query parameters.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<TestResponse>} Test details.
   */
  async fetchTestById(
    id: number,
    params?: TestsQueryParams,
    signal?: AbortSignal | null,
  ): Promise<TestResponse> {
    return this.get<TestResponse>(TESTS_ENDPOINTS.TEST_BY_ID(id), {
      params,
      signal,
    });
  }

  /**
   * Fetch tests by instrument ID with optional filtering.
   * @param {number} instrumentId - Instrument ID.
   * @param {TestsByInstrumentParams} params - Optional filter parameters.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<TestsResponse>} Array of tests for the instrument.
   */
  async fetchTestsByInstrument(
    instrumentId: number,
    params?: TestsByInstrumentParams,
    signal?: AbortSignal | null,
  ): Promise<TestsResponse> {
    return this.get<TestsResponse>(TESTS_ENDPOINTS.TESTS_BY_INSTRUMENT(instrumentId), {
      params,
      signal,
    });
  }

  /**
   * Create a new test.
   * @param {TestCreateRequest} data - Test data.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<TestResponse>} Created test.
   */
  async createTest(data: TestCreateRequest, signal?: AbortSignal | null): Promise<TestResponse> {
    return this.post<TestResponse>(TESTS_ENDPOINTS.CREATE_TEST, {
      body: data,
      signal,
    });
  }

  /**
   * Update an existing test.
   * @param {number} id - Test ID.
   * @param {TestUpdateRequest} data - Updated test data.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<TestResponse>} Updated test.
   */
  async updateTest(
    id: number,
    data: TestUpdateRequest,
    signal?: AbortSignal | null,
  ): Promise<TestResponse> {
    return this.put<TestResponse>(TESTS_ENDPOINTS.UPDATE_TEST(id), {
      body: data,
      signal,
    });
  }

  /**
   * Delete a test.
   * @param {number} id - Test ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<void>}
   */
  async deleteTest(id: number, signal?: AbortSignal | null): Promise<void> {
    return this.delete(TESTS_ENDPOINTS.DELETE_TEST(id), {
      signal,
    });
  }
}
